import { Link } from "react-router-dom";
import { MapPin, Calendar } from "lucide-react";
import { format, parseISO, differenceInDays, isToday, isTomorrow, isWithinInterval, isPast, isValid } from "date-fns";
import { it } from "date-fns/locale";

// Calculate countdown text
const getCountdown = (dataInizio, dataFine) => {
  // Handle invalid or missing dates
  if (!dataInizio || !dataFine) {
    return { text: "Data N/D", type: "future", urgent: false };
  }

  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const start = parseISO(dataInizio);
    const end = parseISO(dataFine);
    
    // Check if dates are valid
    if (!isValid(start) || !isValid(end)) {
      return { text: "Data N/D", type: "future", urgent: false };
    }
    
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    
    // Check if ongoing
    if (isWithinInterval(today, { start, end })) {
      const daysLeft = differenceInDays(end, today);
      if (daysLeft === 0) {
        return { text: "Ultimo giorno", type: "live", urgent: true };
      }
      return { text: "In corso", type: "live", urgent: false };
    }
    
    // Check if already passed
    if (isPast(end)) {
      return { text: "Conclusa", type: "past", urgent: false };
    }
    
    // Future events
    const daysUntil = differenceInDays(start, today);
    
    if (isToday(start)) {
      return { text: "Inizia oggi", type: "today", urgent: true };
    }
    
    if (isTomorrow(start)) {
      return { text: "Domani", type: "soon", urgent: true };
    }
    
    if (daysUntil <= 7) {
      return { text: `Tra ${daysUntil} giorni`, type: "soon", urgent: false };
    }
    
    if (daysUntil <= 30) {
      const weeks = Math.ceil(daysUntil / 7);
      return { text: `Tra ${weeks} settiman${weeks === 1 ? 'a' : 'e'}`, type: "upcoming", urgent: false };
    }
    
    return { text: `Tra ${daysUntil} giorni`, type: "future", urgent: false };
  } catch (error) {
    console.error("Error parsing date:", error);
    return { text: "Data N/D", type: "future", urgent: false };
  }
};

// Format date range for display
const formatDateRange = (dataInizio, dataFine) => {
  try {
    const start = parseISO(dataInizio);
    const end = parseISO(dataFine);
    
    if (!isValid(start)) return "Data N/D";
    
    const startFormatted = format(start, "d MMM yyyy", { locale: it });
    
    if (!isValid(end) || dataInizio === dataFine) {
      return startFormatted;
    }
    
    // Same month and year
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${format(start, "d", { locale: it })} - ${format(end, "d MMM yyyy", { locale: it })}`;
    }
    
    // Same year
    if (start.getFullYear() === end.getFullYear()) {
      return `${format(start, "d MMM", { locale: it })} - ${format(end, "d MMM yyyy", { locale: it })}`;
    }
    
    return `${startFormatted} - ${format(end, "d MMM yyyy", { locale: it })}`;
  } catch {
    return "Data N/D";
  }
};

const CountdownBadge = ({ countdown }) => {
  const styles = {
    live: "bg-green-500 text-white",
    today: "bg-primary text-white",
    soon: "bg-secondary text-foreground",
    upcoming: "bg-muted text-muted-foreground",
    future: "bg-muted text-muted-foreground",
    past: "bg-muted/50 text-muted-foreground"
  };
  
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-md ${styles[countdown.type]} ${countdown.urgent ? 'animate-pulse' : ''}`}>
      {countdown.type === 'live' && <span className="inline-block w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse" />}
      {countdown.text}
    </span>
  );
};

const SagraCard = ({ sagra, index = 0, compact = false }) => {
  const countdown = getCountdown(sagra.data_inizio, sagra.data_fine);
  const dateRange = formatDateRange(sagra.data_inizio, sagra.data_fine);
  
  if (compact) {
    // Compact version for "In corso" section
    return (
      <Link 
       to={`/sagra/${sagra.slug}`}
        className="flex gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
        data-testid={`sagra-card-${sagra.id}`}
      >
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
          <img
            src={sagra.immagine_url || "https://images.unsplash.com/photo-1504674900247-0877df9cc836"}
            alt={sagra.nome}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <CountdownBadge countdown={countdown} />
          </div>
          <h3 className="font-medium text-foreground text-sm truncate">
            {sagra.nome}
          </h3>
          <p className="text-xs text-muted-foreground truncate">
            {sagra.comune}, {sagra.provincia}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link 
      to={`/sagra/${sagra.slug}`} 
      className={`block animate-slide-up stagger-${(index % 4) + 1}`}
      data-testid={`sagra-card-${sagra.id}`}
    >
      <article className="group card-minimal rounded-xl overflow-hidden bg-card">
        {/* Image */}
        <div className="aspect-[3/2] overflow-hidden bg-muted relative image-subtle">
          <img
            src={sagra.immagine_url || "https://images.unsplash.com/photo-1504674900247-0877df9cc836"}
            alt={sagra.nome}
            className="object-cover w-full h-full"
            loading="lazy"
          />
          {/* Distance badge - only show on image */}
          {sagra.distanza_km !== null && sagra.distanza_km !== undefined && (
            <div className="absolute top-2 right-2">
              <span className="text-xs font-medium bg-white/95 backdrop-blur-sm text-foreground px-2 py-0.5 rounded-md shadow-subtle">
                {sagra.distanza_km} km
              </span>
            </div>
          )}
        </div>
        
        {/* Content - Date and countdown outside image */}
        <div className="p-3">
          {/* Date row with countdown badge */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1.5 text-primary">
              <Calendar className="w-3.5 h-3.5" />
              <span className="text-sm font-medium">{dateRange}</span>
            </div>
            <CountdownBadge countdown={countdown} />
          </div>
          
          <h3 className="font-medium text-foreground text-base mb-1 line-clamp-1 group-hover:text-primary transition-colors duration-200">
            {sagra.nome}
          </h3>
          
          <div className="flex items-center gap-1 text-muted-foreground text-sm">
            <MapPin className="w-3.5 h-3.5" />
            <span className="truncate">{sagra.comune}, {sagra.provincia}</span>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default SagraCard;
export { getCountdown, CountdownBadge, formatDateRange };
